import React, { useState, useEffect, useRef } from "react"
import { Card, CardBody, Modal, ModalHeader, ModalBody, ModalFooter, Spinner, Table } from "reactstrap"
import Swal from "sweetalert2"
import {
  fetchNodeData,
  FetchNodeDeleteData,
} from "utils/common-fetchData"
import secureLocalStorage from "react-secure-storage"
import { axiosNodeInstance } from "utils/config"
import configure from "configure"

const EMPTY_FORM = {
  image: null,
  imagePreview: null,
  name: null,
  designation: null,
}

const imageBaseUrl = configure.nodeApi.slice(0, -3);
 

const ZithasPillars = () => {
  const [activeCountry, setActiveCountry] = useState(1)
  const [countries, setCountries] = useState([])
  const [pillars, setPillars] = useState({})
  const [modalOpen, setModalOpen] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState(EMPTY_FORM)
  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(false)

  const fileRef = useRef()
  const token = secureLocalStorage.getItem("auth_token")

  // ── Fetch locations ──────────────────────────────────────────────────────
  useEffect(() => {
    ;(async () => {
      const res = await fetchNodeData(null, "locations", token)
      setCountries(
        res?.data
          ?.filter(i => i.id !== 2)
          ?.map(item => ({ id: item.id, label: item.location })) || []
      )
    })()
  }, [])

  // ── Fetch pillars on tab change ──────────────────────────────────────────
  useEffect(() => {
    fetchPillarList()
  }, [activeCountry])

  const currentPillars = pillars[activeCountry] || []

  // ── Modal helpers ────────────────────────────────────────────────────────
  const openAdd = () => {
    setEditingId(null)
    setForm(EMPTY_FORM)
    setErrors({})
    setModalOpen(true)
  }

  const openEdit = pillar => {
    setEditingId(pillar.id)
    setForm({
      image: null,
      imagePreview: pillar.image
        ? `${pillar.image?.includes("cloudinary") ? "" : imageBaseUrl}${
            pillar.image
          }`
        : null,
      name: pillar.staff_name,
      designation: pillar.designation,
    })
    setErrors({})
    setModalOpen(true)
  }

  // const handleImageChange = e => {
  //   const file = e.target.files[0]
  //   if (!file) return
  //   const reader = new FileReader()
  //   reader.onload = ev =>
  //     setForm(f => ({ ...f, image: file, imagePreview: ev.target.result }))
  //   reader.readAsDataURL(file)
  // }

  const handleImageChange = e => {
    const file = e.target.files[0]
    if (!file) return

    const img = new Image()
    const reader = new FileReader()

    reader.onload = ev => {
      img.src = ev.target.result
    }

    img.onload = () => {
      if (img.width !== 410 || img.height !== 500) {
        Swal.fire({
          icon: "warning",
          title: "Invalid Image Size",
          text: "Please upload an image with dimensions 410 × 500 pixels.",
        })
        return
      }

      // valid image
      setForm(f => ({
        ...f,
        image: file,
        imagePreview: img.src,
      }))
    }

    reader.readAsDataURL(file)
  }

  const validate = () => {
    const e = {}
    if (!form.name) e.name = "Name is required."
    if (!form.designation) e.designation = "Designation is required."
    if (!editingId && !form.image) e.image = "Please upload an image."
    return e
  }

  async function fetchPillarList() {
    const fresh = await fetchNodeData(
      setLoading,
      `pillars/getByCountry/${activeCountry}`,
      token
    )
    if (fresh?.success)
      setPillars(prev => ({ ...prev, [activeCountry]: fresh.data || [] }))
  }

  const handleSave = async () => {
    const e = validate()
    if (Object.keys(e).length) {
      setErrors(e)
      return
    }

    const payload = new FormData()
    payload.append("country_id", activeCountry)
    payload.append("name", form.name)
    payload.append("designation", form.designation)
    if (form.image) payload.append("file", form.image)
    if (editingId) payload.append("id", editingId)

    const res = await axiosNodeInstance.post(
      editingId ? `pillars/update/${editingId}` : "pillars/create",
      payload,
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "multipart/form-data",
        },
      }
    )

    if (res?.data?.success) {
      await fetchPillarList()
      setModalOpen(false)
      Swal.fire({
        icon: "success",
        title: editingId ? "Updated!" : "Added!",
        text: "Pillar saved successfully.",
        timer: 1500,
        showConfirmButton: false,
      })
    }
  }

  const handleDelete = id => {
    Swal.fire({
      icon: "warning",
      title: "Delete Pillar?",
      text: "This action cannot be undone.",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      confirmButtonColor: "#dc2626",
    }).then(async r => {
      if (r.isConfirmed) {
        const res = await FetchNodeDeleteData(setLoading, `pillars/delete`, id)
        if (res?.data?.success) {
          setPillars(prev => ({
            ...prev,
            [activeCountry]: prev[activeCountry].filter(p => p.id !== id),
          }))
          Swal.fire({
            icon: "success",
            title: res?.data?.message || "Deleted!",
            timer: 1200,
            showConfirmButton: false,
          })
        }
      }
    })
  }

  // add this handler in the component
  const handleToggleStatus = async pillar => {
    Swal.fire({
      icon: "warning",
      title: `Are you sure?`,
      text: `You want to ${
        pillar.status === 1 ? "Deactivate " : "Activate "
      } Team.`,
      showCancelButton: true,
      confirmButtonText: "Yes, do it",
      confirmButtonColor: "#dc2626",
    }).then(async r => {
      if (r.isConfirmed) {
        const res = await axiosNodeInstance.patch(
          `pillars/toggle-status/${pillar.id}`
        )

        if (res?.data?.success) {
          setPillars(prev => ({
            ...prev,
            [activeCountry]: prev[activeCountry].map(p =>
              p.id === pillar.id ? { ...p, status: res?.data.status } : p
            ),
          }))
        }
      }
    })
  }

  return (
    <>
      <div className="px-2">
        <div className="mb-4 mt-2 d-flex justify-content-between">
          <p className="text-purple fs-5 mb-0 fw-bold">Pillars of Zithas</p>
        </div>
        <div>
          {/* ── Tabs + Add button ──────────────────────────────────────── */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div className="d-flex gap-2">
              {countries.map(c => (
                <button
                  key={c.id}
                  type="button"
                  className={`btn btn-sm ${
                    activeCountry === c.id
                      ? "btn-purple"
                      : "btn-outline-secondary"
                  }`}
                  onClick={() => setActiveCountry(c.id)}
                >
                  {c.label}
                </button>
              ))}
            </div>

            <button
              type="button"
              className="btn btn-purple btn-label"
              onClick={openAdd}
            >
              <i className="fas fa-plus label-icon me-1" /> Add Pillar
            </button>
          </div>

          {/* ── Loader ────────────────────────────────────────────────── */}
          {loading && (
            <div className="text-center py-4">
              <Spinner size="sm" />
            </div>
          )}

          {/* ── Table ─────────────────────────────────────────────────── */}
          {!loading && (
            <Table className="table table-bordered" responsive>
              <thead>
                <tr>
                  <th className="text-center" style={{ width: 50 }}>
                    #
                  </th>
                  <th style={{ width: 80 }}>Photo</th>
                  <th>Staff Member</th>
                  <th>Designation</th>
                  <th className="text-center" style={{ width: 100 }}>
                    Status
                  </th>
                  <th className="text-center" style={{ width: 120 }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentPillars.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center text-muted py-4">
                      No pillars added for this region yet.
                    </td>
                  </tr>
                ) : (
                  currentPillars.map((pillar, idx) => (
                    <tr key={pillar.id}>
                      <td className="text-center align-middle">{idx + 1}</td>
                      <td className="align-middle">
                        {pillar.image ? (
                          <img
                            src={`${
                              pillar.image?.includes("cloudinary")
                                ? ""
                                : imageBaseUrl
                            }${pillar.image}`}
                            alt={pillar.staff_name}
                            style={{
                              width: 48,
                              height: 48,
                              borderRadius: 8,
                              objectFit: "cover",
                              objectPosition: "top",
                              border: "1px solid #dee2e6",
                            }}
                          />
                        ) : (
                          <div
                            className="d-flex align-items-center justify-content-center text-muted"
                            style={{
                              width: 48,
                              height: 48,
                              borderRadius: 8,
                              background: "#f8f9fa",
                              border: "1px solid #dee2e6",
                              fontSize: 18,
                            }}
                          >
                            <i className="fas fa-user" />
                          </div>
                        )}
                      </td>
                      <td className="align-middle">
                        {pillar.staff_name || "—"}
                      </td>
                      <td className="align-middle">
                        {pillar.designation || "—"}
                      </td>
                      <td className="text-center align-middle">
                        <div className="form-switch form-switch-md">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="customSwitchsizemd"
                            checked={pillar.status === 1}
                            onChange={e => {
                              handleToggleStatus(pillar)
                            }}
                          />
                        </div>
                      </td>
                      <td className="text-center align-middle">
                        <button
                          className="btn btn-sm btn-outline-secondary me-2"
                          onClick={() => openEdit(pillar)}
                          title="Edit"
                        >
                          <i className="fas fa-pen" />
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(pillar.id)}
                          title="Delete"
                        >
                          <i className="fas fa-trash" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </div>
      </div>

      {/* ── Modal ───────────────────────────────────────────────────────── */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} centered>
        <ModalHeader toggle={() => setModalOpen(false)}>
          {editingId ? "Edit Pillar" : "Add Pillar"}
        </ModalHeader>

        <ModalBody>
          {/* Staff select */}
          <div className="mb-3">
            <label className="form-label">
              Full Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              placeholder="Enter Full Name"
              className="form-control"
              value={form.name}
              onChange={n => {
                setForm(f => ({ ...f, name: n.target.value }))
                if (errors.name) setErrors(e => ({ ...e, name: undefined }))
              }}
            />
            {errors.name && (
              <span className="text-danger" style={{ fontSize: "0.8rem" }}>
                {errors.name}
              </span>
            )}
          </div>

          <div className="mb-3">
            <label className="form-label">
              Designation <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              placeholder="Enter Designation"
              className="form-control"
              value={form.designation}
              onChange={n => {
                setForm(f => ({ ...f, designation: n.target.value }))
                if (errors.designation)
                  setErrors(e => ({ ...e, designation: undefined }))
              }}
            />
            {errors.designation && (
              <span className="text-danger" style={{ fontSize: "0.8rem" }}>
                {errors.designation}
              </span>
            )}
          </div>

          {/* Image upload */}
          <div className="row">
            <div className="col-md-4">
              <div className="mb-3">
                <label className="form-label">
                  Photo{" "}
                  {!editingId ? (
                    <span className="text-danger">*</span>
                  ) : (
                    <span
                      className="text-muted"
                      style={{ fontSize: "0.75rem" }}
                    >
                      (leave blank to keep existing)
                    </span>
                  )}
                </label>

                <input
                  type="file"
                  accept="image/*"
                  ref={fileRef}
                  style={{ display: "none" }}
                  onChange={handleImageChange}
                />

                {/* Preview */}
                {form.imagePreview && (
                  <div className="mb-2" style={{ width: "150px" }}>
                    <img
                      src={form.imagePreview}
                      alt="preview"
                      style={{
                        maxWidth: "100%",
                        height: 170,
                        objectFit: "contain",
                        borderRadius: 8,
                        border: "1px solid #dee2e6",
                      }}
                    />
                  </div>
                )}

                {/* Upload trigger */}
                <label style={{ cursor: "pointer", marginBottom: 0 }}>
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handleImageChange}
                  />
                  <span className="btn btn-sm btn-outline-secondary">
                    <i
                      className="fas fa-upload me-1"
                      style={{ fontSize: 11 }}
                    />
                    {form.imagePreview ? "Change Photo" : "Upload Photo"}
                  </span>
                </label>

                {errors.image && (
                  <div
                    className="text-danger mt-1"
                    style={{ fontSize: "0.8rem" }}
                  >
                    {errors.image}
                  </div>
                )}
              </div>
            </div>
            <div className="col-md-8">
              <div className="photo-guidelines px-3 py-2">
                <h6 className="mb-2">Photo guidelines</h6>
                <ul className="guidelines-list mb-0">
                  <li className="text-success">
                    Clear face with good lighting
                  </li>
                  <li className="text-success">
                    Plain background & formal look
                  </li>
                  <li className="text-danger">Image size: 410 × 500 px only</li>
                </ul>
              </div>
            </div>
          </div>
        </ModalBody>

        <ModalFooter>
          <button
            type="button"
            className="btn btn-purple"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <>
                <Spinner size="sm" className="me-2" /> Saving…
              </>
            ) : (
              <>
                <i className="fas fa-save me-2" />
                {editingId ? "Update" : "Save"}
              </>
            )}
          </button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default ZithasPillars
